import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

import Typography from "../typography/Typography";
import styles from "./modalShell.module.scss";

interface ModalShellProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  children?: ReactNode;
  showClose?: boolean;
}

export default function ModalShell({ open, title, onClose, children, showClose = true }: ModalShellProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) onClose();
  };

  // Portal to <body> so the modal escapes any ancestor that creates a
  // containing block for `position: fixed` (transform/filter/contain on
  // a parent traps the fixed positioning inside the parent's box). In
  // the itinerary details right pane this was pinning the modal to the
  // pane instead of centering it on the viewport.
  return createPortal(
    <div className={styles.main}>
      <div className={styles.dialogContainer} onClick={handleBackdropClick}>
        <div className={styles.dialogContent}>
          <section className={styles.dialogBody}>
            {(title || showClose) && (
              <header className={styles.header}>
                {title && <Typography className={styles.title}>{title}</Typography>}
                {showClose && (
                  <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                    <CloseOutlinedIcon className={styles.closeIcon} />
                  </button>
                )}
              </header>
            )}
            {children}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
